import { Reveal } from "@/components/Reveal";
import { Editable } from "@/components/Editable";
import { useApp } from "@/context/AppContext";
import { Sun, Clapperboard, Mountain, Compass } from "lucide-react";

const SECTOR_META = [
  { id: "solar", num: "01", icon: Sun },
  { id: "media", num: "02", icon: Clapperboard },
  { id: "mining", num: "03", icon: Mountain },
];

export default function CompanyProfile() {
  const { t } = useApp();
  return (
    <div data-testid="company-profile">
      {/* Intro */}
      <section className="oj-section">
        <div className="oj-container max-w-4xl text-center">
          <Reveal>
            <div className="oj-eyebrow justify-center mb-4">OOJJAS CORPORATE PRIVATE LIMITED</div>
            <Editable as="h2" id="home.profile.tagline" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0B3B39] tracking-tight leading-tight block">
              {t.profile.tagline}
            </Editable>
            <Editable as="p" id="home.profile.intro1" className="mt-6 text-slate-600 leading-relaxed block">
              {t.profile.intro1}
            </Editable>
            <Editable as="p" id="home.profile.intro2" className="mt-4 text-slate-600 leading-relaxed block">
              {t.profile.intro2}
            </Editable>
          </Reveal>
        </div>
      </section>

      {/* Sector details */}
      <section className="oj-section !pt-0">
        <div className="oj-container space-y-6">
          {SECTOR_META.map((s, i) => {
            const Icon = s.icon;
            const data = t.profile.sectors[s.id];
            return (
              <Reveal key={s.id} delay={i * 0.08}>
                <div className="oj-card p-7 sm:p-9 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start" data-testid={`profile-sector-${s.id}`}>
                  <div className="flex md:flex-col items-center gap-3 md:gap-2 md:w-24 shrink-0">
                    <span className="text-3xl font-extrabold gold-text font-mono">{s.num}</span>
                    <span className="grid place-items-center w-12 h-12 rounded-xl bg-teal-50 border border-teal-200"><Icon className="w-6 h-6 text-teal-600" /></span>
                  </div>
                  <div>
                    <Editable as="h3" id={`home.profile.${s.id}.title`} className="text-xl sm:text-2xl font-extrabold text-[#0B3B39] block">{data.title}</Editable>
                    <div className="mt-3 space-y-3">
                      {data.paras.map((p, j) => (
                        <Editable as="p" key={`${s.id}-p${j}`} id={`home.profile.${s.id}.p${j}`} className="text-slate-600 leading-relaxed block">{p}</Editable>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Our Vision */}
      <section className="bg-[#E9F5F4] border-y border-teal-100">
        <div className="oj-container oj-section max-w-3xl text-center">
          <Reveal>
            <div className="grid place-items-center w-14 h-14 rounded-2xl bg-amber-100 border border-amber-300 mx-auto mb-5"><Compass className="w-7 h-7 text-amber-600" /></div>
            <div className="oj-eyebrow justify-center mb-3">{t.profile.visionEyebrow}</div>
            <Editable as="p" id="home.profile.vision1" className="text-xl sm:text-2xl font-bold text-[#0B3B39] leading-snug block">
              {t.profile.vision1}
            </Editable>
            <Editable as="p" id="home.profile.vision2" className="mt-5 text-slate-700 leading-relaxed block">
              {t.profile.vision2}
            </Editable>
          </Reveal>
        </div>
      </section>

      {/* Our Core Sectors */}
      <section className="oj-section">
        <div className="oj-container">
          <Reveal>
            <div className="oj-eyebrow justify-center mb-3">{t.profile.portfolioFocus}</div>
            <h2 className="oj-h2 text-center mb-10">{t.profile.coreSectors}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.profile.core.map(([title, line], i) => (
              <Reveal key={title} delay={i * 0.08}>
                <div className="oj-card p-7 h-full text-center border-t-4 !border-t-amber-400" data-testid={`core-sector-${i}`}>
                  <h4 className="font-extrabold text-[#0B3B39] tracking-wide">{title}</h4>
                  <p className="mt-3 text-sm font-medium text-amber-600 leading-relaxed">{line}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="text-center mt-14">
              <div className="oj-eyebrow justify-center mb-3">OOJJAS CORPORATE PRIVATE LIMITED</div>
              <Editable as="h3" id="home.profile.closing" className="text-2xl sm:text-3xl font-extrabold text-[#0B3B39] block">{t.profile.closing}</Editable>
              <p className="mt-3 font-mono text-sm font-bold gold-text tracking-widest">{t.profile.closingTag}</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
